import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Skeleton,
  Tooltip,
  Collapse,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  Close as CloseIcon,
  Circle as CircleIcon,
  FileDownload as DownloadIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as ViewIcon,
  Download as FileDownloadIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { chequeService } from '../../services/cheques/chequeService';
import { chequeAttachmentService } from '../../services/cheques/chequeAttachmentService';
import { useBankService } from '../../services/banks/bankService';
import { companyPreferencesService } from '../../services/settings/companyPreferencesService';
import { useSnackbar } from '../../hooks/useSnackbar';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { exportToExcel } from '../../utils/excelExport';
import { formatCurrencyAmount, getCurrencyDecimals } from '../../utils/currencyUtils';

// ─── Constants ───────────────────────────────────────────

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_COLORS = {
  Pending:   { bg: '#fef3c7', color: '#d97706', dot: '#f59e0b' },
  Printed:   { bg: '#ede9fe', color: '#7c3aed', dot: '#8b5cf6' },
  Signed:    { bg: '#ede7f6', color: '#5e35b1', dot: '#7e57c2' },
  Collected: { bg: '#dbeafe', color: '#2563eb', dot: '#3b82f6' },
  Cleared:   { bg: '#d1fae5', color: '#059669', dot: '#10b981' },
  Cancelled: { bg: '#fee2e2', color: '#dc2626', dot: '#ef4444' },
  Bounced:   { bg: '#fce7f3', color: '#be185d', dot: '#ec4899' },
};

const VISIBLE_STATUSES = ['Pending', 'Printed', 'Collected', 'Cleared'];

// ─── Styled Components ───────────────────────────────────

const PageContainer = styled(Box)({
  height: '100%',
  backgroundColor: '#f1f5f9',
  padding: '24px',
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
});

const CalendarCard = styled(Box)({
  backgroundColor: '#ffffff',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  boxShadow: 'none',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
});

const WeekdayHeader = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  borderBottom: '2px solid #e2e8f0',
});

const WeekdayCell = styled(Typography)(({ isFriday }) => ({
  textAlign: 'center',
  padding: '10px 0',
  fontSize: '0.75rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: isFriday ? '#dc2626' : '#64748b',
}));

const CalendarGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  flex: 1,
  minHeight: 0,
});

const DayCell = styled(Box)(({ isToday, isCurrentMonth, isFriday, hasData }) => ({
  borderRight: '1px solid #f1f5f9',
  borderBottom: '1px solid #f1f5f9',
  padding: '6px',
  minHeight: '90px',
  backgroundColor: isToday ? '#eff6ff' : !isCurrentMonth ? '#f8fafc' : '#fff',
  opacity: isCurrentMonth ? 1 : 0.5,
  cursor: hasData ? 'pointer' : 'default',
  transition: 'background-color 0.15s',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  '&:hover': hasData ? {
    backgroundColor: isToday ? '#dbeafe' : '#f0f4ff',
  } : {},
  '&:nth-of-type(7n)': { borderRight: 'none' },
}));

const DayNumber = styled(Typography)(({ isToday, isFriday }) => ({
  fontSize: '0.82rem',
  fontWeight: isToday ? 800 : 600,
  color: isToday ? '#2563eb' : isFriday ? '#dc2626' : '#0f172a',
  width: isToday ? 26 : 'auto',
  height: isToday ? 26 : 'auto',
  borderRadius: isToday ? '50%' : 0,
  backgroundColor: isToday ? '#2563eb' : 'transparent',
  color: isToday ? '#fff' : isFriday ? '#dc2626' : '#0f172a',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  flexShrink: 0,
}));

const ChequeChip = styled(Box)(({ statusColor }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 6px',
  borderRadius: '4px',
  backgroundColor: statusColor || '#f1f5f9',
  marginBottom: '2px',
  cursor: 'pointer',
  '&:hover': { opacity: 0.85 },
}));

// ─── Helpers ─────────────────────────────────────────────

const getCalendarDays = (year, month) => {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const current = new Date(startDate);
  // Always show 6 weeks for consistent height
  for (let i = 0; i < 42; i++) {
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return days;
};

const dateKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const formatAmount = (amount, currency = 'AED') => formatCurrencyAmount(amount, currency);

const isDateToday = (d) => {
  const today = new Date();
  return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
};

// ─── Component ───────────────────────────────────────────

const PDCCalendarPage = () => {
  const { showError } = useSnackbar();
  const bankService = useBankService();
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [banks, setBanks] = useState([]);
  const [selectedBankId, setSelectedBankId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [signedStatusEnabled, setSignedStatusEnabled] = useState(false);

  // Optional "Signed Status" module — only show Signed on the calendar/filter when enabled
  useEffect(() => {
    let active = true;
    companyPreferencesService.getPreferences().then((result) => {
      if (active && result.success) setSignedStatusEnabled(Boolean(result.data?.enableSignedStatus));
    });
    return () => { active = false; };
  }, []);

  const visibleStatuses = useMemo(
    () => (signedStatusEnabled
      ? ['Pending', 'Printed', 'Signed', 'Collected', 'Cleared']
      : VISIBLE_STATUSES),
    [signedStatusEnabled]
  );

  // Fetch all cheques (same approach as Reports page)
  const loadCheques = useCallback(async () => {
    setLoading(true);
    try {
      const response = await chequeService.getPrintedCheques({
        pageNumber: 1,
        pageSize: 10000,
        status: '',
      });

      if (response.success && response.data) {
        const data = Array.isArray(response.data) ? response.data : [];
        setCheques(data);
      } else {
        showError(response.message || 'Failed to load cheques');
        setCheques([]);
      }
    } catch {
      showError('Error loading calendar data');
      setCheques([]);
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadCheques();
    const loadBanks = async () => {
      try {
        const result = await bankService.getAllBanks(1, 10000);
        if (result.success || result.Success) {
          const data = result.data || result.Data || [];
          setBanks(Array.isArray(data) ? data.map(b => ({
            id: b.Id || b.id,
            accountName: b.AccountName || b.accountName || '',
            currency: b.Currency || b.currency || '',
          })) : []);
        }
      } catch { /* silent */ }
    };
    loadBanks();
  }, [loadCheques]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normalize and group cheques by cheque date (exclude Cancelled & Bounced)
  const chequesByDate = useMemo(() => {
    const map = {};
    cheques.forEach((c) => {
      const status = c.Status || c.status || 'Pending';
      // Always exclude Cancelled & Bounced from calendar
      if (!visibleStatuses.includes(status)) return;
      // Status filter
      if (selectedStatus !== 'all' && status !== selectedStatus) return;

      // Bank filter
      if (selectedBankId !== 'all') {
        const bankId = c.BankId || c.bankId;
        if (String(bankId) !== String(selectedBankId)) return;
      }

      const raw = c.ChqDate || c.chqDate;
      if (!raw) return;
      const d = new Date(raw);
      if (isNaN(d.getTime())) return;
      const key = dateKey(d);
      if (!map[key]) map[key] = [];
      map[key].push({
        Id: c.Id || c.id,
        ChequeNumber: c.ChequeNumber || c.chequeNumber || '',
        BeneficiaryName: c.BeneficiaryName || c.beneficiaryName || '',
        ChqAmount: c.ChqAmount || c.chqAmount || 0,
        ChqDate: raw,
        Status: status,
        BankName: c.BankName || c.bankName || '',
        Currency: c.Currency || c.currency || 'AED',
        Remarks: c.Remarks || c.remarks || '',
      });
    });
    return map;
  }, [cheques, selectedBankId, selectedStatus, visibleStatuses]);

  // Summary stats for the current month from grouped data
  const monthStats = useMemo(() => {
    let count = 0;
    let total = 0;
    let pending = 0;
    let printed = 0;
    Object.entries(chequesByDate).forEach(([key, dayCheques]) => {
      const d = new Date(key);
      if (d.getMonth() !== currentMonth || d.getFullYear() !== currentYear) return;
      dayCheques.forEach((c) => {
        count++;
        total += Number(c.ChqAmount || 0);
        if (c.Status === 'Pending') pending++;
        else if (c.Status === 'Printed') printed++;
      });
    });
    return { count, total, pending, printed };
  }, [chequesByDate, currentMonth, currentYear]);

  const calendarDays = useMemo(
    () => getCalendarDays(currentYear, currentMonth),
    [currentYear, currentMonth]
  );

  const goToToday = () => {
    setCurrentYear(today.getFullYear());
    setCurrentMonth(today.getMonth());
  };

  const goPrev = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const goNext = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const handleDayClick = (day) => {
    const key = dateKey(day);
    if (chequesByDate[key]?.length > 0) {
      setSelectedDate(day);
    }
  };

  const selectedCheques = selectedDate ? (chequesByDate[dateKey(selectedDate)] || []) : [];
  const selectedTotal = selectedCheques.reduce((sum, c) => sum + Number(c.ChqAmount || 0), 0);

  // ─── Excel Export ───────────────────────────────────────
  const handleExportExcel = () => {
    const monthCheques = [];
    Object.entries(chequesByDate).forEach(([key, dayCheques]) => {
      const d = new Date(key);
      if (d.getMonth() !== currentMonth || d.getFullYear() !== currentYear) return;
      dayCheques.forEach((c) => monthCheques.push(c));
    });

    if (monthCheques.length === 0) {
      showError('No cheques to export for this month');
      return;
    }

    const totalAmount = monthCheques.reduce((sum, c) => sum + Number(c.ChqAmount || 0), 0);
    const monthName = MONTH_NAMES[currentMonth];
    const exportCurrencies = new Set(monthCheques.map((c) => c.Currency || c.currency || 'AED'));
    const exportDecimals = exportCurrencies.size === 1 ? getCurrencyDecimals([...exportCurrencies][0]) : 2;

    exportToExcel({
      title: `PDC Calendar — ${monthName} ${currentYear}`,
      fileName: `EasyCheck_PDC_Calendar_${monthName}-${currentYear}.xlsx`,
      sheetName: 'PDC Calendar',
      columns: [
        { header: 'Cheque #', key: 'ChequeNumber', type: 'text' },
        { header: 'Beneficiary', key: 'BeneficiaryName', type: 'text', width: 30 },
        { header: 'Bank', key: 'BankName', type: 'text', width: 20 },
        { header: 'Amount', key: 'ChqAmount', type: 'amount', decimals: exportDecimals },
        { header: 'Currency', key: 'Currency', type: 'text' },
        { header: 'Date', key: 'ChqDateFormatted', type: 'text' },
        { header: 'Status', key: 'Status', type: 'text' },
      ],
      data: monthCheques.map((c) => ({
        ...c,
        ChqDateFormatted: formatDateForDisplay(new Date(c.ChqDate)),
      })),
      parameters: [
        { label: 'Month', value: `${monthName} ${currentYear}` },
        { label: 'Total Cheques', value: String(monthCheques.length) },
      ],
      totals: { 'Amount': totalAmount },
    });
  };

  // ─── Attachment State ──────────────────────────────────
  const [expandedAttachments, setExpandedAttachments] = useState({});
  const [attachmentsData, setAttachmentsData] = useState({});
  const [attachmentsLoading, setAttachmentsLoading] = useState({});

  // Reset attachment state when dialog closes
  useEffect(() => {
    if (!selectedDate) {
      setExpandedAttachments({});
      setAttachmentsData({});
      setAttachmentsLoading({});
    }
  }, [selectedDate]);

  const toggleAttachments = async (chequeId) => {
    const isExpanded = expandedAttachments[chequeId];
    setExpandedAttachments((prev) => ({ ...prev, [chequeId]: !isExpanded }));

    // Fetch attachments if not already loaded
    if (!isExpanded && !attachmentsData[chequeId]) {
      setAttachmentsLoading((prev) => ({ ...prev, [chequeId]: true }));
      try {
        const response = await chequeAttachmentService.getAttachments(chequeId);
        if (response.success) {
          const items = Array.isArray(response.data) ? response.data : [];
          setAttachmentsData((prev) => ({ ...prev, [chequeId]: items }));
        } else {
          setAttachmentsData((prev) => ({ ...prev, [chequeId]: [] }));
        }
      } catch {
        setAttachmentsData((prev) => ({ ...prev, [chequeId]: [] }));
      } finally {
        setAttachmentsLoading((prev) => ({ ...prev, [chequeId]: false }));
      }
    }
  };

  const handleViewAttachment = async (attachment) => {
    try {
      const result = await chequeAttachmentService.downloadAttachment(
        attachment.Id || attachment.id
      );
      if (result.success && result.data) {
        const url = result.data.url || result.data.Url;
        if (url) window.open(url, '_blank');
      }
    } catch {
      showError('Failed to view attachment');
    }
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const result = await chequeAttachmentService.downloadAttachment(
        attachment.Id || attachment.id
      );
      if (result.success && result.data) {
        const url = result.data.url || result.data.Url;
        const fileName = result.data.fileName || attachment.FileName || attachment.fileName || 'attachment';
        if (url) {
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          if (result.data.isBlob) URL.revokeObjectURL(url);
        }
      }
    } catch {
      showError('Failed to download attachment');
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width={300} height={36} />
          <Skeleton variant="rounded" width={200} height={36} />
        </Box>
        <Skeleton variant="rounded" sx={{ flex: 1, borderRadius: '12px' }} />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            PDC Calendar
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.3 }}>
            Post-dated cheques by payment date
          </Typography>
        </Box>

        {/* Bank filter + Month stats chips + export */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={selectedBankId}
              onChange={(e) => setSelectedBankId(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: 600,
                '& .MuiSelect-select': { py: 0.8 },
              }}
            >
              <MenuItem value="all">All Bank Accounts</MenuItem>
              {banks.map((bank) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.accountName} ({bank.currency})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              displayEmpty
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: 600,
                '& .MuiSelect-select': { py: 0.8 },
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>
              {visibleStatuses.map((s) => (
                <MenuItem key={s} value={s}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircleIcon sx={{ fontSize: 8, color: STATUS_COLORS[s].dot }} />
                    {s}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Chip label={`${monthStats.count} cheques`} sx={{ backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 600, fontSize: '0.78rem' }} />
          <Chip label={formatAmount(monthStats.total)} sx={{ backgroundColor: '#f0fdf4', color: '#059669', fontWeight: 600, fontSize: '0.78rem' }} />
          {monthStats.pending > 0 && (
            <Chip label={`${monthStats.pending} pending`} size="small" sx={{ backgroundColor: STATUS_COLORS.Pending.bg, color: STATUS_COLORS.Pending.color, fontWeight: 600, fontSize: '0.72rem' }} />
          )}
          {monthStats.printed > 0 && (
            <Chip label={`${monthStats.printed} printed`} size="small" sx={{ backgroundColor: STATUS_COLORS.Printed.bg, color: STATUS_COLORS.Printed.color, fontWeight: 600, fontSize: '0.72rem' }} />
          )}
          <Tooltip title="Export month to Excel">
            <IconButton
              onClick={handleExportExcel}
              size="small"
              sx={{ color: '#059669', backgroundColor: '#f0fdf4', '&:hover': { backgroundColor: '#d1fae5' }, ml: 0.5 }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Calendar Card */}
      <CalendarCard>
        {/* Month navigation */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={goPrev} size="small" sx={{ color: '#64748b' }}>
              <ChevronLeft />
            </IconButton>
            <Typography sx={{ fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', minWidth: 180, textAlign: 'center' }}>
              {MONTH_NAMES[currentMonth]} {currentYear}
            </Typography>
            <IconButton onClick={goNext} size="small" sx={{ color: '#64748b' }}>
              <ChevronRight />
            </IconButton>
          </Box>
          <Tooltip title="Go to today">
            <IconButton onClick={goToToday} size="small" sx={{ color: '#2563eb' }}>
              <TodayIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Weekday headers */}
        <WeekdayHeader>
          {WEEKDAYS.map((day, i) => (
            <WeekdayCell key={day} isFriday={i === 5}>
              {day}
            </WeekdayCell>
          ))}
        </WeekdayHeader>

        {/* Calendar grid */}
        <CalendarGrid>
          {calendarDays.map((day, idx) => {
            const key = dateKey(day);
            const dayCheques = chequesByDate[key] || [];
            const isCurrentMonth = day.getMonth() === currentMonth;
            const isFriday = day.getDay() === 5;
            const isTodayDate = isDateToday(day);
            const dayTotal = dayCheques.reduce((sum, c) => sum + Number(c.ChqAmount || 0), 0);
            const maxVisible = 3;

            return (
              <DayCell
                key={idx}
                isToday={isTodayDate}
                isCurrentMonth={isCurrentMonth}
                isFriday={isFriday}
                hasData={dayCheques.length > 0}
                onClick={() => isCurrentMonth && handleDayClick(day)}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                  <DayNumber isToday={isTodayDate} isFriday={isFriday}>
                    {day.getDate()}
                  </DayNumber>
                  {dayCheques.length > 0 && isCurrentMonth && (
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#059669', fontFamily: 'monospace' }}>
                      {formatAmount(dayTotal)}
                    </Typography>
                  )}
                </Box>

                {/* Cheque indicators */}
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  {isCurrentMonth && dayCheques.slice(0, maxVisible).map((cheque, i) => {
                    const colors = STATUS_COLORS[cheque.Status] || STATUS_COLORS.Pending;
                    const name = cheque.BeneficiaryName;

                    return (
                      <ChequeChip key={i} statusColor={colors.bg}>
                        <CircleIcon sx={{ fontSize: 6, color: colors.dot }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: colors.color, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {name}
                        </Typography>
                      </ChequeChip>
                    );
                  })}
                  {isCurrentMonth && dayCheques.length > maxVisible && (
                    <Typography sx={{ fontSize: '0.62rem', color: '#2563eb', fontWeight: 600, pl: 0.5 }}>
                      +{dayCheques.length - maxVisible} more
                    </Typography>
                  )}
                </Box>
              </DayCell>
            );
          })}
        </CalendarGrid>

        {/* Status legend */}
        <Box sx={{ display: 'flex', gap: 2.5, p: 1.5, borderTop: '1px solid #e2e8f0', justifyContent: 'center' }}>
          {Object.entries(STATUS_COLORS).filter(([s]) => s !== 'Cancelled' && s !== 'Bounced').map(([status, colors]) => (
            <Box key={status} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CircleIcon sx={{ fontSize: 8, color: colors.dot }} />
              <Typography sx={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 500 }}>{status}</Typography>
            </Box>
          ))}
        </Box>
      </CalendarCard>

      {/* Day Detail Dialog */}
      <Dialog
        open={!!selectedDate}
        onClose={() => setSelectedDate(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        {selectedDate && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#0f172a' }}>
                  {formatDateForDisplay(selectedDate)}
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }}>
                  {selectedCheques.length} cheque{selectedCheques.length !== 1 ? 's' : ''} — Total: {formatAmount(selectedTotal)}
                </Typography>
              </Box>
              <IconButton onClick={() => setSelectedDate(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              {/* Table header */}
              <Box sx={{ display: 'flex', px: 1, py: 1, borderBottom: '2px solid #f1f5f9' }}>
                <Typography sx={{ flex: 0.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8' }}>Chq #</Typography>
                <Typography sx={{ flex: 1.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8' }}>Beneficiary</Typography>
                <Typography sx={{ flex: 1, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8' }}>Bank</Typography>
                <Typography sx={{ flex: 0.8, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8', textAlign: 'right' }}>Amount</Typography>
                <Typography sx={{ flex: 0.5, fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', color: '#94a3b8', textAlign: 'center' }}>Status</Typography>
              </Box>

              {selectedCheques.map((cheque, i) => {
                const status = cheque.Status;
                const colors = STATUS_COLORS[status] || STATUS_COLORS.Pending;
                const name = cheque.BeneficiaryName;
                const chqNum = cheque.ChequeNumber;
                const amount = cheque.ChqAmount;
                const currency = cheque.Currency;
                const bank = cheque.BankName;
                const chequeId = cheque.Id;
                const isExpanded = expandedAttachments[chequeId];
                const attachments = attachmentsData[chequeId] || [];
                const isLoadingAttachments = attachmentsLoading[chequeId];

                return (
                  <Box key={i}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        py: 1.2,
                        borderBottom: '1px solid #f8fafc',
                        '&:hover': { backgroundColor: '#fafbfc' },
                      }}
                    >
                      <Typography sx={{ flex: 0.5, fontSize: '0.85rem', fontWeight: 600, fontFamily: 'monospace', color: '#0f172a' }}>
                        {chqNum}
                      </Typography>
                      <Typography sx={{ flex: 1.5, fontSize: '0.85rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {name}
                      </Typography>
                      <Typography sx={{ flex: 1, fontSize: '0.78rem', color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {bank}
                      </Typography>
                      <Typography sx={{ flex: 0.8, fontSize: '0.85rem', fontWeight: 700, fontFamily: 'monospace', color: '#0f172a', textAlign: 'right' }}>
                        {formatAmount(amount, currency)}
                      </Typography>
                      <Box sx={{ flex: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Chip
                          label={status}
                          size="small"
                          sx={{
                            height: 22,
                            fontSize: '0.68rem',
                            fontWeight: 600,
                            backgroundColor: colors.bg,
                            color: colors.color,
                          }}
                        />
                        {chequeId && (
                          <Tooltip title={isExpanded ? 'Hide attachments' : 'Show attachments'}>
                            <IconButton
                              size="small"
                              onClick={() => toggleAttachments(chequeId)}
                              sx={{ p: 0.3, color: '#64748b' }}
                            >
                              <AttachFileIcon sx={{ fontSize: 16 }} />
                              {isExpanded ? <ExpandLessIcon sx={{ fontSize: 14, ml: -0.5 }} /> : <ExpandMoreIcon sx={{ fontSize: 14, ml: -0.5 }} />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>

                    {/* Attachments expandable section */}
                    {chequeId && (
                      <Collapse in={isExpanded}>
                        <Box sx={{ pl: 3, pr: 1, py: 1, backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                          {isLoadingAttachments ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <CircularProgress size={14} />
                              <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>Loading attachments...</Typography>
                            </Box>
                          ) : attachments.length === 0 ? (
                            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontStyle: 'italic', py: 0.5 }}>
                              No attachments
                            </Typography>
                          ) : (
                            attachments.map((att, j) => {
                              const fileName = att.FileName || att.fileName || 'File';
                              const docType = att.DocumentTypeName || att.documentTypeName || '';
                              const contentType = att.ContentType || att.contentType || '';
                              const isViewable = contentType.startsWith('image/') || contentType === 'application/pdf';

                              return (
                                <Box
                                  key={j}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 0.5,
                                    '&:not(:last-child)': { borderBottom: '1px solid #e2e8f0' },
                                  }}
                                >
                                  <FileIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                      {fileName}
                                    </Typography>
                                    {docType && (
                                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{docType}</Typography>
                                    )}
                                  </Box>
                                  {isViewable && (
                                    <Tooltip title="View">
                                      <IconButton size="small" onClick={() => handleViewAttachment(att)} sx={{ p: 0.3, color: '#2563eb' }}>
                                        <ViewIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  <Tooltip title="Download">
                                    <IconButton size="small" onClick={() => handleDownloadAttachment(att)} sx={{ p: 0.3, color: '#059669' }}>
                                      <FileDownloadIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                );
              })}

              {/* Total row */}
              <Box sx={{ display: 'flex', px: 1, py: 1.2, mt: 0.5, backgroundColor: '#f8fafc', borderRadius: '8px' }}>
                <Typography sx={{ flex: 2, fontSize: '0.85rem', fontWeight: 700, color: '#0f172a' }}>Total</Typography>
                <Typography sx={{ flex: 1, fontSize: '0.85rem' }} />
                <Typography sx={{ flex: 0.8, fontSize: '0.85rem', fontWeight: 800, fontFamily: 'monospace', color: '#0f172a', textAlign: 'right' }}>
                  {formatAmount(selectedTotal)}
                </Typography>
                <Box sx={{ flex: 0.5 }} />
              </Box>
            </DialogContent>
          </>
        )}
      </Dialog>
    </PageContainer>
  );
};

export default PDCCalendarPage;
